import React, { useEffect, useMemo, HTMLAttributes } from "react";
import _ from 'lodash';
import cx from 'classnames';
import styles from './entity.module.scss';
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "../../../../ui/animation";
import InfiniteScroll from "../../../../ui/infiniteScrollLatest";
import { useSpring, animated, AnimatedProps } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { fetchDataSourceChildren } from "../../../../../actions/dataSourceInspector";
import Truncate from "../../../../ui/truncate";
import { Icon } from 'semantic-ui-react';
import { FiChevronDown, FiChevronRight } from "react-icons/fi";


interface EntityBrowserProps {
  mappings?: any[];
  className?: string;
  width: number;
  entity?: any;
  items?: any[];
  action?: (entity: any) => React.ReactNode;
  onSelection: (entity: any) => void;
  parent?: string;
  mapping?: string;
  onMappingSelection: (string: any) => void;
  selectedFolder?: string;
  loading?: boolean;
  setAuthToast?: any;
}
const AnimatedSpan = animated.span as React.FC<AnimatedProps<HTMLAttributes<HTMLDivElement>>>;

const EntityBrowser: React.FC<EntityBrowserProps> = ({ loading, className, width, items, action, onSelection, parent, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {
  return (
    <div className={cx(styles.container, className)}>
      {items && items.map((item) => (
        <EntityRow key={item.id} entity={item} width={width} action={action} onSelection={onSelection} parent={parent} mapping={mapping || ''} onMappingSelection={onMappingSelection} selectedFolder={selectedFolder} setAuthToast={setAuthToast} />
      ))}
    </div>
  );
}
const MappingsBrowser: React.FC<EntityBrowserProps> = ({ mappings, className, width, action, onSelection, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {
  const loading = useSelector((state: any) => state.dataSourceInspector.mappings.loading);

  return (
    <div className={cx(styles.container, styles.stacks)} style={{ height: selectedFolder ? "66vh" : "100vh" }}>
      <Reveal show={loading} className={styles.stack}>
        <MappingPlaceholder width={'50%'} />
        <MappingPlaceholder width={'70%'} />
        <MappingPlaceholder width={'60%'} />
        <MappingPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser loading={loading} className={className} items={mappings} width={width} action={action} onSelection={onSelection} mapping={mapping} onMappingSelection={onMappingSelection} selectedFolder={selectedFolder} setAuthToast={setAuthToast} />
      </Reveal>
    </div>
  );
}
const FolderBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection, mapping, selectedFolder, onMappingSelection }) => {
  const dispatch = useDispatch();

  const dataSourceItems = useSelector((state: any) => state.dataSourceInspector.items);
  const source = dataSourceItems.find((item: any) => item.id === entity?.id);
  var items = source?.children?.items || [];
  const loading = source?.children?.loading && !source?.fetchedChildren;

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchDataSourceChildren({ account: entity.id, parent: "", showFile: false }));
    }
  }, [entity?.id, dispatch]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              <FolderPlaceholder />
            </>
          )
        }}
      >
        <EntityBrowser loading={loading} className={className} width={width} items={items} action={action} onSelection={onSelection} parent={entity?.id} mapping={mapping} selectedFolder={selectedFolder} onMappingSelection={onMappingSelection} />
      </InfiniteScroll>
    </div>
  );
}
interface EntityRowProps {
  entity: any;
  width: number;
  action?: (entity: any) => React.ReactNode;
  onSelection: (entity: any) => void;
  parent?: string;
  mapping: string;
  onMappingSelection: (id: string) => void;
  selectedFolder?: string;
  setAuthToast?: any;
}
const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, parent, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {
  const Component = useMemo(() => {
    const isStorage = entity?.storageType;

    if (isStorage) {
      return MappingRow;
    }
    return FolderRow;
  }, [entity]);
  if (!Component) {
    return null;
  }
  return (
    React.createElement(Component, { entity, width, action, onSelection, parent, mapping, onMappingSelection, selectedFolder, setAuthToast })
  );
}

const FolderRow: React.FC<EntityRowProps> = ({
  entity,
  width,
  action,
  onSelection,
  parent,
  mapping,
  selectedFolder,
  onMappingSelection,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure({ debounce: 100 });
  const [childrenAnimation, api] = useSpring(() => ({
    opacity: 0,
    height: 0,
    config: { tension: 200, friction: 20 },
  }));

  React.useEffect(() => {
    if (expanded) {
      api.start({
        opacity: 1,
        height: containerBounds.height || 0,
      });
    } else {
      api.start({
        opacity: 0,
        height: 0,
        immediate: false,
        onRest: () => {
          if (!expanded) {
            setMountChildren(false);
          }
        },
      });
    }
  }, [expanded, containerBounds.height, api]);

  const handleClick = () => {
    const willExpand = !expanded;

    if (entity?.type === 'FILE') {
      setExpanded(willExpand);
      if (willExpand) {
        setMountChildren(true);
      }
      onSelection(entity);
      onMappingSelection(mapping);
      return;
    }

    setExpanded(willExpand);
    if (willExpand) {
      setMountChildren(true);
    }
    if (willExpand) {
      onSelection(entity);
      onMappingSelection(mapping);
    }
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, {
          [styles.expanded]: expanded,
        })}
        style={{ width: width }}
        onClick={handleClick}
      >
        <div className={styles.name}>
          <div className={styles.leftIcon}>
            <Icon name={entity?.type === 'FILE' ? "file outline" : "folder"} size="small" />
          </div>
          <Truncate width={240}>{entity?.name}</Truncate>
        </div>

        <div className={cx(styles.actions, styles.secondaryAction)}>
          <button className={cx(styles.action)}>
            {expanded || selectedFolder === entity?.id ? (
              <FiChevronDown className={styles.arrow} />
            ) : (
              <FiChevronRight className={styles.arrow} />
            )}
          </button>
        </div>
      </div>
      {mountChildren && (
        <AnimatedSpan style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <FileBrowser
              entity={entity}
              width={width - 15}
              action={action}
              onSelection={onSelection}
              parent={parent}
              mapping={mapping}
              selectedFolder={selectedFolder}
              onMappingSelection={onMappingSelection}
            />
          </div>
        </AnimatedSpan>
      )}
    </>
  );
};

const FileBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection, parent, mapping, selectedFolder, onMappingSelection }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (entity.id && entity.type !== 'FILE') {
      dispatch(fetchDataSourceChildren({
        account: parent || entity.id,
        parent: parent ? entity.id : "",
        showFile: false
      }));
    }
  }, [entity.id, dispatch, parent]);

  const dataSourceItems = useSelector((state: any) => state.dataSourceInspector.items);
  const source = dataSourceItems.find((item: any) => item.id === mapping);
  const loading = source?.children?.[entity.id]?.loading;

  var items = [];
  if (parent && source && source.children) {
    if (entity && entity.id) {
      items = source.children[entity.id]?.items || [];
    }
  }

  return (
    <InfiniteScroll
      className={styles.scroll}
      loading={loading}
      hasMore={false}
      loadMore={() => { }}
      loader={() => {
        return (
          <>
            <FolderPlaceholder />
          </>
        );
      }}
    >
      <div className={styles.subContainer}>
        <EntityBrowser
          loading={loading}
          className={className}
          width={width}
          items={items}
          action={action}
          mapping={mapping}
          onSelection={onSelection}
          parent={parent}
          selectedFolder={selectedFolder}
          onMappingSelection={onMappingSelection}
        />
      </div>
    </InfiniteScroll>
  );
}

const MappingRow: React.FC<EntityRowProps> = ({
  entity,
  width,
  action,
  onSelection,
  mapping,
  onMappingSelection,
  selectedFolder,
  setAuthToast,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure({ debounce: 100 });
  const [childrenAnimation, api] = useSpring(() => ({
    opacity: 0,
    height: 0,
    config: { tension: 200, friction: 20 },
  }));

  React.useEffect(() => {
    if (expanded) {
      api.start({
        opacity: 1,
        height: containerBounds.height || 0,
      });
    } else {
      api.start({
        opacity: 0,
        height: 0,
        immediate: false,
        onRest: () => {
          if (!expanded) {
            setMountChildren(false);
          }
        },
      });
    }
  }, [expanded, containerBounds.height, api]);

  const handleClick = () => {
    if (entity.migrationStatus === "AUTH_ERROR" && setAuthToast) {
      setAuthToast(true, entity.id);
      return;
    }

    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand) {
      setMountChildren(true);
    }
    onMappingSelection(entity.id);
    onSelection(entity);
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, {
          [styles.expanded]: expanded,
        })}
        style={{ width: width }}
        onClick={handleClick}
      >
        <div className={cx(styles.mappingIcon, styles[entity.storageType])}></div>
        <div className={styles.name}>

          <Truncate width={240}>
            {entity?.name} {entity?.storageName}
          </Truncate>
        </div>

        <div className={cx(styles.actions, styles.secondaryAction)}>
          <button className={cx(styles.action)}>
            {expanded || selectedFolder === entity?.id ? (
              <FiChevronDown className={styles.arrow} />
            ) : (
              <FiChevronRight className={styles.arrow} />
            )}
          </button>
        </div>
      </div>
      {mountChildren && (
        <AnimatedSpan style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <FolderBrowser
              entity={entity}
              width={width - 15}
              action={action}
              onSelection={onSelection}
              mapping={entity.id}
              selectedFolder={selectedFolder}
              onMappingSelection={onMappingSelection}
            />
          </div>
        </AnimatedSpan>
      )}
    </>
  );
};

export const MappingPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const FolderPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { EntityBrowser, MappingsBrowser };